import type { DataProvider, GetListParams, GetListResult, SortPayload } from "react-admin";
import { z } from "zod";
import {
  backendListResponseToRAGetListResult,
  getAuthenticatedBackendClient,
  reactAdminToDRFPagination,
} from "../api/backend.api";
import type { ManualPayment, ManualPaymentEndpoint } from "../generated/backendClient";
import { AbstractDataProvider } from "./AbstractDataProvider";

const manualPaymentTypeSchema = z.enum(["cash", "bank_transfer", "external_bank_transfer"]);

const listFilterSchema = z.object({
  drivingSchoolId: z.string(),
  paymentType: z.preprocess((value) => (value === "" ? undefined : value), manualPaymentTypeSchema.optional()),
  serviceTime: z.string().optional(),
});

export type ManualPaymentType = z.infer<typeof manualPaymentTypeSchema>;

export type ManualPaymentRecord = Omit<ManualPayment, "id" | "payment_type"> & {
  id: string;
  payment_type: ManualPaymentType;
};

type Ordering = NonNullable<Parameters<ManualPaymentEndpoint["manualPaymentList"]>[0]["ordering"]>;

class ManualPaymentsProvider extends AbstractDataProvider<ManualPaymentRecord> {
  async getList(
    _resource: string,
    { pagination, sort, filter }: GetListParams,
  ): Promise<GetListResult<ManualPaymentRecord>> {
    const parsedFilter = listFilterSchema.parse(filter);
    const backendClient = await getAuthenticatedBackendClient();
    const response = await backendClient.manualPayment.manualPaymentList({
      ...reactAdminToDRFPagination(pagination),
      ...parsedFilter,
      ordering: sortToOrdering(sort),
    });
    const results = response.results.map((record): ManualPaymentRecord => {
      if (!record.id) {
        throw new Error("Manual payment response is missing an id");
      }
      return {
        ...record,
        id: record.id,
        payment_type: manualPaymentTypeSchema.parse(record.payment_type),
      };
    });
    return backendListResponseToRAGetListResult({ ...response, results });
  }
}

function sortToOrdering(sort: SortPayload): Ordering {
  return `${sort.order === "ASC" ? "" : "-"}${sortFieldToOrderingField(sort.field)}` as Ordering;
}

function sortFieldToOrderingField(field: string): Ordering {
  switch (field) {
    case "created_at":
      return "created_at";
    case "payment_type":
      return "payment_type";
    case "student_name":
      return "student_name";
    case "amount":
      return "amount";
    default:
      throw new Error(`Unexpected sort field: ${field}`);
  }
}

export const manualPaymentsProvider = new ManualPaymentsProvider() as DataProvider;
